'use client'

import { SoloViewerTokenFragment } from '@/gql/generated/graphql'
import { DetailText, AsteriskIcon2, BodyText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { getVerticalTitle } from '@utils/project'
import { cn } from '@/lib/utils'

// TODO: This should mostly align with the Artworks tab's grid item styling, so can likely remove most of this component in the future

type ShowcaseEditGridItemProps = {
  token: SoloViewerTokenFragment
  isSelected: boolean
  selectedIndex: number
  onToggle: (tokenId: string) => void
}

export const ShowcaseEditGridItem = ({
  token,
  isSelected,
  selectedIndex,
  onToggle
}: ShowcaseEditGridItemProps): React.ReactElement => {
  const isRendered = !!token.image_id

  return (
    <div
      className={cn(
        'rounded-xs group relative cursor-pointer border',
        isSelected ? 'border-outline' : 'hover:border-outline-variant border-transparent'
      )}
      onClick={() => onToggle(token.id)}
    >
      <div className="relative flex w-full flex-col">
        {/* Image Section */}
        {isRendered ? (
          <div className={`bg-surface-low rounded-xs relative aspect-square h-auto`}>
            <div className="relative h-full w-full">
              <ImageWithCustomLoader
                className="p-sm drop-shadow-[3px_3px_6px_rgba(0,0,0,0.20)]"
                src={token.media_url ?? ''}
                blurHash={token.image?.metadata?.blurhash}
                fill
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center'
                }}
                priority
                alt={`${token.invocation}`}
                sizes="33vw"
              />
            </div>
          </div>
        ) : (
          <div className="bg-surface-container gap-y-xs flex h-full min-h-[150px] w-full flex-col items-center justify-center">
            <DetailText className="font-medium">{`Rendering #${token.invocation}`}</DetailText>
          </div>
        )}

        {/* Text Section */}
        <div className="gap-y-xs py-sm flex flex-col text-center">
          <DetailText className="text-on-surface-variant font-medium uppercase">
            {token.project ? getVerticalTitle(token.project) : 'Unknown Project'}
          </DetailText>
          <BodyText className="text-on-surface font-medium">
            {token.project?.name ? `${token.project.name} #${token.invocation}` : `Token #${token.invocation}`}
          </BodyText>
          <BodyText>{token.project?.artist_name || 'Unknown Artist'}</BodyText>
        </div>
      </div>

      {/* Hover indicator */}
      <div className="border-outline-variant bg-surface p-2xs rounded-xs absolute right-2 top-2 z-10 border opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <AsteriskIcon2 className="fill-on-surface-variant h-full w-full" />
      </div>

      {/* Selection indicator */}
      {isSelected ? (
        <div className="border-outline-variant bg-surface p-2xs rounded-xs absolute right-2 top-2 z-10 border">
          <div className="gap-xs flex h-full w-full items-center justify-center">
            <BodyText className="text-on-surface h-full w-[22px] flex-shrink-0 text-center">
              {selectedIndex + 1}
            </BodyText>
            <div className="flex h-full w-1/2 flex-shrink-0 items-center justify-center">
              <AsteriskIcon2 className="fill-on-surface" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
